import { useEffect, useState, useContext } from 'react';
import { Modal, Descriptions, Form, Select, Button, Space, Flex, Result, Spin } from 'antd';
import axios from 'axios';
import { apiBasePath } from '../../../utils/constants';
import { GlobalContext } from '../../../utils/globalContext';

const FALLBACK_MOTIVOS = [
  { value: '01', label: '01 - Comprobantes emitidos con errores con relación.' },
  { value: '02', label: '02 - Comprobantes emitidos con errores sin relación.' },
  { value: '03', label: '03 - No se llevó a cabo la operación.' },
  { value: '04', label: '04 - Operación nominativa relacionada en una factura global.' }
];

export const InvoiceManagementModal = ({ open, record, onClose, onSuccess }) => {
  const { user } = useContext(GlobalContext);

  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [uuidRelacionado, setUuidRelacionado] = useState(null);
  const [loaderCancel, setLoaderCancel] = useState(false);

  // Dynamic options
  const [motivoOptions, setMotivoOptions] = useState([]);
  const [loadingMotivos, setLoadingMotivos] = useState(false);
  const [uuidOptions, setUuidOptions] = useState([]);
  const [loadingUuids, setLoadingUuids] = useState(false);

  // Action result state: null = form view, { status, title, subtitle } = result view
  const [actionResult, setActionResult] = useState(null);

  // Reset local state when opened or record changes
  useEffect(() => {
    if (open) {
      setMotivoCancelacion('');
      setUuidRelacionado(null);
      setMotivoOptions([]);
      setUuidOptions([]);
      setActionResult(null);
    }
  }, [open, record]);

  // Fetch Motivos de cancelación
  useEffect(() => {
    if (open && record) {
      const fetchMotivos = async () => {
        setLoadingMotivos(true);
        try {
          const response = await axios.get(`${apiBasePath}/reference-data/motivo-cancelacion`);
          const list = response?.data?.lstMotivoCancelacion;
          if (list && Array.isArray(list) && list.length > 0) {
            const mapped = list.map((item) => ({
              value: item.codigo,
              label: `${item.codigo} - ${item.descripcion}`
            }));
            setMotivoOptions(mapped);
          } else {
            setMotivoOptions(FALLBACK_MOTIVOS);
          }
        } catch (error) {
          console.error('Error fetching motivos de cancelacion:', error);
          setMotivoOptions(FALLBACK_MOTIVOS);
        } finally {
          setLoadingMotivos(false);
        }
      };
      fetchMotivos();
    }
  }, [open, record]);

  // Fetch Comprobantes relacionados when Motivo '01' is selected
  useEffect(() => {
    if (open && record && motivoCancelacion === '01') {
      const fetchStampedInvoices = async () => {
        setLoadingUuids(true);
        try {
          const clieId = record.oper_clie_id || 0;
          const response = await axios.get(`${apiBasePath}/invoice/stamped/clie_id/${clieId}/stamped/1`);
          const list = response?.data?.listInvoice;
          if (list && Array.isArray(list)) {
            const mapped = list
              .filter((inv) => inv.oper_uuid)
              .map((inv) => ({
                value: inv.oper_uuid,
                label: `${inv.oper_id} - ${inv.oper_uuid}`
              }));
            setUuidOptions(mapped);
          } else {
            setUuidOptions([]);
          }
        } catch (error) {
          console.error('Error fetching stamped invoices:', error);
          setUuidOptions([]);
        } finally {
          setLoadingUuids(false);
        }
      };
      fetchStampedInvoices();
    } else {
      setUuidOptions([]);
    }
  }, [open, record, motivoCancelacion]);

  const handleCloseAfterResult = () => {
    if (actionResult?.status === 'success' && onSuccess) {
      onSuccess();
    }
    setActionResult(null);
    onClose();
  };

  const handleCancelInvoice = async () => {
    setLoaderCancel(true);
    try {
      const loggedUserId = user?.clie_id || user?.user_id_int || 0;

      const payload = {
        cates_id_int: record.oper_cates_id_int || 0,
        clie_id: record.oper_clie_id || 0,
        fcanc_motivo_id: motivoCancelacion,
        invoice_replace_uuid: motivoCancelacion === '01' ? uuidRelacionado : null,
        invoice_uuid: record.oper_uuid || null,
        oper_id: record.oper_id || 0,
        user_id: loggedUserId
      };

      console.log('Cancel request payload:', payload);
      await axios.post(`${apiBasePath}/invoice/cancel`, payload);

      const actionLabel = motivoCancelacion === '01' ? 'sustituida' : 'cancelada';
      setActionResult({
        status: 'success',
        title: `Factura ${actionLabel} correctamente`,
        subtitle: `La factura #${record?.['Num. Oper.'] ?? record?.oper_id} ha sido ${actionLabel} exitosamente.`
      });
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      const errorMsg = error?.response?.data?.message || 'Ocurrió un error inesperado al procesar la solicitud.';
      setActionResult({
        status: 'error',
        title: 'Error al procesar la factura',
        subtitle: errorMsg
      });
    } finally {
      setLoaderCancel(false);
    }
  };

  const mostrarBotonCancelar = ['02', '03', '04'].includes(motivoCancelacion);
  const mostrarSelectUUID = motivoCancelacion === '01';

  return (
    <Modal
      title="Gestión de Factura"
      open={open}
      onCancel={actionResult ? handleCloseAfterResult : onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      {record && (
        <>
          {/* Resultado de la acción */}
          {actionResult ? (
            <Result
              status={actionResult.status}
              title={actionResult.title}
              subTitle={actionResult.subtitle}
              extra={[
                <Button
                  key="close"
                  type="primary"
                  onClick={handleCloseAfterResult}
                >
                  Cerrar
                </Button>
              ]}
            />
          ) : (
            <>
              <Descriptions
                bordered
                size="small"
                column={2}
                style={{ marginBottom: '1.25rem' }}
              >
                <Descriptions.Item label="Núm. Operación" span={1}>
                  {record['Num. Oper.'] ?? record.oper_id ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="RFC" span={1}>
                  {record['RFC'] ?? record.rfc ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Fecha" span={1}>
                  {record['Fecha Oper.'] ?? record.oper_fecha ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo" span={1}>
                  {record['Tipo'] ?? record.invoice_type ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Total" span={1}>
                  {record['Total'] ?? record.oper_total ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Estado" span={1}>
                  {record.estado ?? '—'}
                </Descriptions.Item>
              </Descriptions>

              <Form layout="vertical">
                <Form.Item label="Motivo de cancelación" required>
                  <Select
                    showSearch
                    loading={loadingMotivos}
                    value={motivoCancelacion || undefined}
                    placeholder="Seleccione un motivo"
                    onChange={(val) => {
                      setMotivoCancelacion(val);
                      setUuidRelacionado(null);
                    }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={motivoOptions}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                {/* Motivo 01: selector de UUID / Num. Operación relacionado */}
                {mostrarSelectUUID && (
                  <Form.Item
                    label="Comprobante relacionado (UUID / Núm. de Operación)"
                    required
                  >
                    <Select
                      showSearch
                      allowClear
                      loading={loadingUuids}
                      value={uuidRelacionado}
                      placeholder="Buscar por UUID o número de operación..."
                      onChange={(val) => setUuidRelacionado(val)}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={uuidOptions}
                      style={{ width: '100%' }}
                      notFoundContent={loadingUuids ? <Spin size="small" /> : 'Sin resultados'}
                    />
                  </Form.Item>
                )}

                {/* Motivos 02, 03, 04: botón de cancelar */}
                {mostrarBotonCancelar && (
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Flex justify="flex-end">
                      <Space>
                        <Button onClick={onClose}>Cerrar</Button>
                        <Button
                          danger
                          type="primary"
                          loading={loaderCancel}
                          disabled={!motivoCancelacion}
                          onClick={handleCancelInvoice}
                        >
                          Cancelar Factura
                        </Button>
                      </Space>
                    </Flex>
                  </Form.Item>
                )}

                {/* Motivo 01: botón de cancelar con UUID requerido */}
                {mostrarSelectUUID && (
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Flex justify="flex-end">
                      <Space>
                        <Button onClick={onClose}>Cerrar</Button>
                        <Button
                          danger
                          type="primary"
                          loading={loaderCancel}
                          disabled={!uuidRelacionado}
                          onClick={handleCancelInvoice}
                        >
                          Sustituir Factura
                        </Button>
                      </Space>
                    </Flex>
                  </Form.Item>
                )}
              </Form>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

